import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { internetApi } from '../services/apiServices/user/internet';
import { AccountTypes } from '../models/Common';
import { iptvApi } from '../services/apiServices/user/iptv';
import { ctvApi } from '../services/apiServices/user/ctv';
import { unionApi } from '../services/apiServices/user/union';
import { useDateLocalization } from './useDateLocalization';
import { Devices } from '../models/Accounts';
import { paymentsApi } from '../services/apiServices/user/payments';
import DateVO from '../core/valueObjects/DateVO';

export interface AccountInfo {
  serviceName: string;
  expireDate: Date | string;
  status: string;
  statusWithExpire: string;
  statusId: number;
  connectionType: string;
  servicePrice: string | number;
  isDaily: boolean;
  isInternet: boolean;
  internetLogin: string;
  internetPassword: string;
  internetMac: string;
  internetIp: string;
  internetId: number;
  brizTvEmail: string;
  brizTvPassword: string;
  brizTvPhone: string;
  isBrizTv: boolean;
  isIptv: boolean;
  iptvId: string | number;
  iptvDevices: Devices[] | [];
  isCtv: boolean;
  ctvId: number;
  isUnion: boolean;
  serviceId: number;
  connectionProtocolId: number;
}

const initialState: AccountInfo = {
  serviceId: 0,
  serviceName: '',
  expireDate: '',
  status: '',
  statusWithExpire: '',
  statusId: 0,
  connectionType: '',
  servicePrice: '',
  isDaily: false,
  isInternet: false,
  internetLogin: '',
  internetPassword: '',
  internetMac: '',
  internetIp: '',
  internetId: 0,
  brizTvEmail: '',
  brizTvPassword: '',
  brizTvPhone: '',
  isBrizTv: false,
  isIptv: false,
  iptvId: '',
  iptvDevices: [],
  isCtv: false,
  ctvId: 0,
  isUnion: false,
  connectionProtocolId: 0,
};

export const useAccountInfo = (accountId: number | string, accountType: AccountTypes | '') => {
  const [accountInfo, setAccountInfo] = useState<AccountInfo>(initialState);
  const { formatDate } = useDateLocalization();
  const { t } = useTranslation();
  const { data: internetInfo } = internetApi.useGetInternetInfoQuery(
    { accountId: `${accountId}` },
    { skip: accountType !== AccountTypes.Internet },
  );
  const { data: iptvInfo } = iptvApi.useGetIptvInfoQuery(
    { accountId: `${accountId}` },
    { skip: accountType !== AccountTypes.Iptv },
  );
  const { data: ctvInfo } = ctvApi.useGetCtvInfoQuery(
    { accountId: `${accountId}` },
    { skip: accountType !== AccountTypes.Ctv },
  );
  const { data: unionInfo } = unionApi.useGetUnionInfoQuery(
    { accountId: `${accountId}` },
    { skip: accountType !== AccountTypes.Union },
  );
  const {
    data: servicePresets,
    isError,
    error,
  } = paymentsApi.useGetServicePresetsQuery({ accountId, accountType }, { skip: !accountType });

  const serviceName =
    internetInfo?.Service.Name || iptvInfo?.Service.Name || ctvInfo?.Service.Name || unionInfo?.Service.Name || '';
  const expireDate =
    internetInfo?.RealExpireDate ||
    iptvInfo?.RealExpireDate ||
    ctvInfo?.RealExpireDate ||
    unionInfo?.RealExpireDate ||
    '';
  const status = t('serviceManagement.serviceNonActive');
  const statusWithExpire = `${t('serviceManagement.serviceActive')} ${formatDate(
    DateVO.create(expireDate.toString()).get(),
    {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      locale: 'uk',
    },
  )}`;

  const serviceId =
    internetInfo?.Service.id ?? iptvInfo?.Service.id ?? ctvInfo?.Service.id ?? unionInfo?.Service.id ?? 0;
  const statusId = internetInfo?.StatusID ?? iptvInfo?.StatusID ?? ctvInfo?.StatusID ?? unionInfo?.StatusID ?? -1;
  const connectionType = unionInfo?.Internet?.ConnectionType || internetInfo?.ConnectionType || '';
  const servicePrice =
    internetInfo?.Service.Price || iptvInfo?.Service.Price || ctvInfo?.Service.Price || unionInfo?.Service.Price || '';
  const isDaily =
    internetInfo?.Service.Daily ||
    iptvInfo?.Service.Daily ||
    ctvInfo?.Service.Daily ||
    unionInfo?.Service.Daily ||
    false;

  const isInternet = !!internetInfo || !!unionInfo?.Internet || false;
  const internetLogin = internetInfo?.Access.Login || unionInfo?.Internet?.Access.Login || '';
  const internetPassword = internetInfo?.Access?.Password || unionInfo?.Internet?.Access?.Password || '**********';
  const internetMac = internetInfo?.Access.MAC || unionInfo?.Internet?.Access.MAC || '';
  const internetIp = internetInfo?.Access.IP || unionInfo?.Internet?.Access.IP || '';
  const internetId = internetInfo?.id || unionInfo?.Internet?.id || 0;

  const brizTvEmail = iptvInfo?.BrizTV?.Email || unionInfo?.Iptv?.BrizTV?.Email || '';
  const brizTvPasswordUnion = unionInfo?.Iptv?.BrizTV?.Password;
  const brizTvPhone = iptvInfo?.BrizTV?.Phone || unionInfo?.Iptv?.BrizTV?.Phone || '';
  const isBrizTv = !!iptvInfo?.BrizTV || !!unionInfo?.Iptv?.BrizTV || false;

  const isIptv = !!iptvInfo || !!unionInfo?.Iptv || false;
  const iptvId = iptvInfo?.id || unionInfo?.Iptv?.id || '';
  const iptvDevices = iptvInfo?.Devices || unionInfo?.Iptv?.Devices || [];
  const brizTvPasswordIptv = iptvInfo?.BrizTV?.Password;
  const brizTvPassword = brizTvPasswordUnion || brizTvPasswordIptv || '**********';

  const isCtv = !!ctvInfo || !!unionInfo?.Ctv;
  const ctvId = ctvInfo?.id || unionInfo?.Ctv?.id || 0;

  const isUnion = !!unionInfo;
  const connectionProtocolId = unionInfo?.ConnectionProtocol?.id || internetInfo?.ConnectionProtocol?.id || 0;

  useEffect(() => {
    setAccountInfo({
      serviceId,
      serviceName,
      expireDate,
      status,
      statusWithExpire,
      statusId,
      connectionType,
      servicePrice,
      isDaily,
      isInternet,
      internetLogin,
      internetPassword,
      internetMac,
      internetIp,
      internetId,
      brizTvEmail,
      brizTvPassword,
      brizTvPhone,
      isBrizTv,
      isIptv,
      iptvId,
      iptvDevices,
      isCtv,
      ctvId,
      isUnion,
      connectionProtocolId,
    });
  }, [internetInfo, iptvInfo, ctvInfo, unionInfo, servicePresets]);

  return { accountInfo, error, isError, servicePresets };
};
